import React, { JSX, useCallback, useState } from 'react';
import ReactFlow, {
  addEdge,
  MiniMap,
  Background,
  useNodesState,
  useEdgesState,
  ReactFlowProvider,
  useReactFlow,
  applyNodeChanges,
  applyEdgeChanges,
  Node,
  Edge,
  OnConnect,
  Connection,
  NodeChange,
  EdgeChange,
} from 'reactflow';

import 'reactflow/dist/style.css';
import './WorkflowEditor.css';

import Sidebar from './Sidebar/Sidebar';
import CommonNode, { NodeData } from './CommonNode/CommonNode';
import ControlModal from './Modal/ControlModal'; // modal component
import IndustryTemplatesModal from './flowBuilderTemplates';


type FlowNodeData = {
  label: string;
  icon?: JSX.Element;
  type?: string;
  settings?: Record<string, any>; // or define stricter types per node type
};
// Type definitions
// type NodeData = {
//   label: string;
//   icon: string;
//   onOpenModal: (id: string) => void;
// };

type FlowCanvasProps = {
  onOpenModal: (id: string) => void;
  nodes: Node<NodeData>[];
  setNodes: React.Dispatch<React.SetStateAction<Node<NodeData>[]>>;
  edges: Edge[];
  setEdges: React.Dispatch<React.SetStateAction<Edge[]>>;
};

// nodeTypes config
const nodeTypes = {
  commonNode: CommonNode,
};
const initialNodes: Node<NodeData>[] = [
  {
    id: '1',
    type: 'StartNode',
    data: {
      label: 'Start Credit Review',
      icon: '🟢',
      onOpenModal: () => {},
    },
    position: { x: 100, y: 50 },
  },
  {
    id: '2',
    type: 'DataSourceInput',
    data: {
      label: 'Load Data (Snowflake / SQL / Oracle)',
      icon: '💾',
      onOpenModal: () => {},
    },
    position: { x: 100, y: 180 },
  },
  {
    id: '3',
    type: 'SelficAgentLLM',
    data: {
      label:
        'Selfic Agent: Analyze request, retrieve schema/doc context, generate logic (LLM or custom algo), apply policies',
      icon: '🤖',
      onOpenModal: () => {},
    },
    position: { x: 300, y: 180 },
  },
  {
    id: '4',
    type: 'PolicyCheck',
    data: {
      label: 'Validate Business Rules & Limits',
      icon: '🛡️',
      onOpenModal: () => {},
    },
    position: { x: 500, y: 180 },
  },
  {
    id: '5',
    type: 'ConditionNode',
    data: {
      label: 'Eligible for Credit?',
      icon: '🔍',
      onOpenModal: () => {},
    },
    position: { x: 700, y: 180 },
  },
  {
    id: '6',
    type: 'AutoApprove',
    data: {
      label: 'Auto Approve',
      icon: '✅',
      onOpenModal: () => {},
    },
    position: { x: 900, y: 100 },
  },
  {
    id: '7',
    type: 'ManualReview',
    data: {
      label: 'Escalate for Manual Review',
      icon: '🧑‍⚖️',
      onOpenModal: () => {},
    },
    position: { x: 900, y: 260 },
  },
  {
    id: '8',
    type: 'NotifyAndLog',
    data: {
      label: 'Notify and Log Decision',
      icon: '📤',
      onOpenModal: () => {},
    },
    position: { x: 1100, y: 180 },
  },
];

const initialEdges: Edge[] = [
  { id: 'e1-2', source: '1', target: '2' },
  { id: 'e2-3', source: '2', target: '3' },
  { id: 'e3-4', source: '3', target: '4' },
  { id: 'e4-5', source: '4', target: '5' },
  { id: 'e5-6', source: '5', target: '6', label: 'Yes' },
  { id: 'e5-7', source: '5', target: '7', label: 'No' },
  { id: 'e6-8', source: '6', target: '8' },
  { id: 'e7-8', source: '7', target: '8' },
];



const FlowCanvas: React.FC<FlowCanvasProps> = ({
  onOpenModal,
  nodes,
  setNodes,
  edges,
  setEdges,
}) => {
  
  const reactFlowInstance = useReactFlow();

  const onNodesChange = useCallback(
    (changes: NodeChange[]) => {
      setNodes((nds) => applyNodeChanges(changes, nds));
    },
    [setNodes]
  );

  const onEdgesChange = useCallback(
    (changes: EdgeChange[]) => {
      setEdges((eds) => applyEdgeChanges(changes, eds));
    },
    [setEdges]
  );

  const onConnect: OnConnect = useCallback(
    (params: Connection) => {
      setEdges((eds) => addEdge(params, eds));
    },
    [setEdges]
  );

  const onDrop = useCallback(
    (event: React.DragEvent<HTMLDivElement>) => {
      event.preventDefault();
      const type = event.dataTransfer.getData('application/reactflow');
      if (!type) return;

      const bounds = event.currentTarget.getBoundingClientRect();
      const position = reactFlowInstance.project({
        x: event.clientX - bounds.left,
        y: event.clientY - bounds.top,
      });

      const newNode: Node<NodeData> = {
        id: `${+new Date()}`,
        type: 'commonNode',
        position,
        data: {
          label: `${type} node`,
          icon: '📦',
          onOpenModal,
        },
      };

      setNodes((nds) => nds.concat(newNode));
    },
    [reactFlowInstance, setNodes, onOpenModal]
  );

  const onDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
  };
  const [selectedNodeId, setSelectedNodeId] = useState<string | null>(null);

  return (
    <div className="workflow-canvas" onDrop={onDrop} onDragOver={onDragOver}>
      <ReactFlow
        nodes={nodes.map((node) => ({
          ...node,
          data: { ...node.data, onOpenModal },
        }))}
        edges={edges}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        onConnect={onConnect}
        nodeTypes={nodeTypes}
        fitView
        proOptions={{ hideAttribution: true }}
        onNodeDoubleClick={(event, node) => setSelectedNodeId(node.id)}
      >
        <MiniMap />
        <Background />
      </ReactFlow>
    </div>
  );
};

const WorkflowEditor: React.FC = () => {
  const [nodes, setNodes] = useState<Node<NodeData>[]>(initialNodes);
  const [edges, setEdges] = useState<Edge[]>(initialEdges);
  const [selectedNodeId, setSelectedNodeId] = useState<string | null>(null);


  const openModal = (nodeId: string) => {
    setSelectedNodeId(nodeId);
  };

  const closeModal = () => setSelectedNodeId(null);
  const [showTemplatesModal, setShowTemplatesModal] = useState(false);


  const saveNodeData = (label: string, type: string, settings: Record<string, any>) => {
    setNodes((nds) =>
      nds.map((node) =>
        node.id === selectedNodeId
          ? {
              ...node,
              data: {
                ...node.data,
                label,
                type,
                settings,
              },
            }
          : node
      )
    );
    setSelectedNodeId(null);
  };

  const deleteNode = () => {
    setNodes((nds) => nds.filter((node) => node.id !== selectedNodeId));
    setEdges((eds) => eds.filter((edge) => edge.source !== selectedNodeId && edge.target !== selectedNodeId));
    setSelectedNodeId(null);
  };


  const saveFlow = () => {
    const flowData = {
      nodes,
      edges,
    };
    localStorage.setItem('flowData', JSON.stringify(flowData));
    alert('✅ Flow saved successfully!');
  };

  const loadFlow = () => {
    const flowData = localStorage.getItem('flowData');
    if (flowData) {
      const parsedFlow = JSON.parse(flowData);
      setNodes(parsedFlow.nodes || []);
      setEdges(parsedFlow.edges || []);
      alert('📂 Flow loaded successfully!');
    } else {
      alert('⚠️ No saved flow found.');
    }
  };
  const selectedNode = nodes.find((n) => n.id === selectedNodeId);


  return (
    <div>
      <div className="workflow-container">
        <Sidebar />
        <div className="workflow-topbar">
          <div className="workflow-actions">
            <button onClick={saveFlow} className="btn save-btn">
              💾 
            </button>
            <button onClick={() => setShowTemplatesModal(true)} className="btn load-btn">
              📂
            </button>
          </div>
        </div>

        <ReactFlowProvider>
          <FlowCanvas
            nodes={nodes}
            setNodes={setNodes}
            edges={edges}
            setEdges={setEdges}
            onOpenModal={openModal}
          />
        </ReactFlowProvider>

        {selectedNodeId && (
           <ControlModal
           node={selectedNode!}
           onSave={saveNodeData}
           onDelete={deleteNode}
           onClose={closeModal}
         />
        )}
      </div>

       {showTemplatesModal && 
       (
        <IndustryTemplatesModal onClose={() => setShowTemplatesModal(false)} />
      )}
    </div>
  );
};

export default WorkflowEditor;
